import React, { useState, useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";
import { initiateMediaUpload, updateMediaUploadProgress, uploadMedia, searchMedia, deleteMedia, invalidateUploadError, fetchingExternalLibrary, invalidateMediaLibrary, invalidateMediaDeleteError } from "actions/media";

import _ from "lodash";
import cx from "classnames";
import Dropzone from 'react-dropzone';
import S3Upload from "react-s3-uploader/s3upload";
import AuthService from "services/auth.service";
import shortid from "shortid";
import { Modal, Col, Row, Button, Nav, ProgressBar, Tab, InputGroup, Form, Dropdown } from "react-bootstrap";
import { FileUploadButton } from "components/Buttons";
import { IKImage } from 'imagekitio-react';
import InfiniteScroll from "components/InfiniteScroll";
import { PaletteContext } from "components/Palette";
import Truncate from "react-truncate";
import { ExternalMediaView } from "./ExternalMedia";

import styles from "./library.module.scss";
import { toast } from "react-toastify";
import { FailureToast } from "components/Toast";
import { BrandMediaView } from "./BrandMedia";
import StoryboardContext from 'components/Storyboard/context';
import { fetchPalettes, fetchPalettesForWorkspace } from "actions/palette";
import { actionGroup } from "actions/editor";

const API_URL = `${process.env.REACT_APP_PAPERFLITE_API_URL}/api/2.0`;
const IMAGE_CDN_ENDPOINT = process.env.REACT_APP_IMAGE_CDN;

export function MediaLibraryModal({ show, onChange, multiple, storyboard, target, mediaTypes = ['image', 'video'], onSelect, onClose, editorSession, mediaTarget}) {
  const paletteContext = React.useContext(PaletteContext);

  const defaultPalettes = useSelector(state => _.get(state, 'palette.default.items', []));
  const brandkitPalettes = useSelector(state => _.get(state, 'palette.brandkit.items', []));

  const paletteDatas = defaultPalettes.map(item => {
    const brandkitItem = brandkitPalettes.find(brandkitItem => brandkitItem.type === item.type);
    return  {
      name: item.name,
      type: item.type,
      defaultId: item.id,
      brandkitId: _.get(brandkitItem, 'id', null),
      showDefault: !_.get(brandkitItem, 'enabled', true),
    }
  });

  const dispatch = useDispatch();
  const [selected, setSelected] = useState([]);
  const [uploadInstances, setUploadInstances] = useState([]);
  const [mediaType, setMediaType] = useState("image");
  const [mediaLibraryType, setMediaLibraryType] = useState("pexels");
  const [activeState, setActiveState] = useState("manageMedia");
  const [orientation, setOrientation] = useState("");
  const uploadError = useSelector(state => _.get(state, 'media.uploadError'));
  const deleting = useSelector(state => _.get(state, 'media.deleting'));

  const palettes = _.get(paletteContext, 'palettes', []).length == 0 ? paletteDatas : _.get(paletteContext, 'palettes', []);
  const paletteData = palettes.find(item => item.type === 'MEDIA');
  const brandkitId = _.get(paletteData, 'brandkitId', null);

  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item"));
  const brandkitEnabled = _.get(workspace, 'brandkitEnabled', false);

  const storyboardContext = React.useContext(StoryboardContext);

  useEffect(() => {
    dispatch(fetchPalettesForWorkspace());
    dispatch(fetchPalettes());
  }, []);

  useEffect(() => {
    if (!show || deleting) {
      setSelected([]);
    }
  }, [show, deleting]);

  const handleClick = (media) => {
    setSelected((selected) => {
      var updated = [...selected];

      if (multiple) {
        const exists = updated.find(m => m.id === media.id) != null;
        if (exists) {
          updated = updated.filter(m => m.id !== media.id);
        } else {
          updated = [...updated, media];
        }
      } else {
        updated = [media];
      }

      return updated;
    });
  }

  const handleClose = () => {
    paletteContext.close();

    if (onClose) {
      onClose();
    }
    if (uploadError) {
      dispatch(invalidateUploadError());
    }
  }

  const handleUploadInstanceChange = (instance) => {
    setUploadInstances((state) => {
      return state.map(item => {
        if (item.files[0].id === instance.files[0].id) {
          return instance
        }

        return item;
      })
    });
  }

  const handleManageMediaClick = () => {
    setActiveState("manageMedia");
  }

  const handleExternalMedia = () => {
    setActiveState("pexels");
  }

  return (
    <Modal show={show} className={cx(styles.confirmation, styles.dialog)} backdropClassName={styles.overlay} centered>
      <Modal.Header>
        <div className={styles.mediaLibraryHeader}>
          Media Library
        </div>
        {onClose &&
          <div className={styles.closeButton}>
            <i className={cx("cleverstory-close", styles.closeIcon)} onClick={handleClose} />
          </div>
        }
      </Modal.Header>
      <Modal.Body className={styles.body}>
        <Tab.Pane>
          <Tab.Container id="left-tabs-example" defaultActiveKey="manageMedia">
            <div className={styles.mediaLibrary}>
              <div className={styles.mediaTypes}>
                <Nav variant="pills" >
                  <MediaUploadButton
                    storyboard={storyboard}
                    target={target}
                    mediaTypes={mediaTypes}
                    onInstanceChange={handleUploadInstanceChange}
                    mediaTarget={mediaTarget} 
                  />
                  <Nav.Item>
                    <Nav.Link eventKey="manageMedia" className={styles.tabLinks} onClick={handleManageMediaClick}>
                      <i className={cx("cleverstory-background", styles.icon)} />My Uploads</Nav.Link>
                  </Nav.Item>
                  <hr />
                  <p className={styles.mediaTypeName}>Stock Media</p>
                  {brandkitEnabled && 
                  <Nav.Item>
                    <Nav.Link eventKey="brandMedia" className={styles.tabLinks} onClick={handleExternalMedia} >
                      <i className={cx("cleverstory-brandmedia", styles.icon)} />Brand Media</Nav.Link>
                    </Nav.Item>}
                  <Nav.Item>
                    <Nav.Link eventKey="pexels" className={styles.tabLinks} onClick={handleExternalMedia} >
                      <i className={cx("cleverstory-pexels", styles.icon)} />Pexels</Nav.Link>
                  </Nav.Item>
                </Nav>
              </div>

              <div className={styles.mediaList}>
                <Tab.Content>
                  <Tab.Pane eventKey="manageMedia">
                    <MediaGalleryView
                      mediaTypes={mediaTypes}
                      storyboard={storyboard}
                      target={target}
                      onSelect={handleClick}
                      selected={selected}
                      editable={true}
                      mediaTarget={mediaTarget}
                      uploadInstances={uploadInstances}
                      onInstanceChange={handleUploadInstanceChange}
                    />
                  </Tab.Pane>
                  <Tab.Pane eventKey="brandMedia">
                    <BrandMediaView
                      palette={brandkitId}
                      mediaTypes={mediaTypes}
                      onSelect={handleClick}
                      multiple={multiple}
                      editable={true}
                      storyboard={storyboardContext}
                    />
                  </Tab.Pane>
                  <Tab.Pane eventKey="pexels">
                    <ExternalMediaView
                      mediaTypes={mediaTypes}
                      onSelect={onSelect}
                      active={activeState}
                      orientation={orientation}
                      mediaLibraryType={mediaLibraryType}
                      onClose={onClose}
                      onChange={onChange}
                      editorSession={editorSession}
                      target={target}
                      editable={true}
                      mediaTarget={mediaTarget}
                    />
                  </Tab.Pane>
                </Tab.Content>
              </div>
            </div>
          </Tab.Container>
        </Tab.Pane>
      </Modal.Body>
      <Modal.Footer>
        <Button size="sm"
          variant="primary"
          disabled={_.size(selected) === 0}
          onClick={() => {
            onSelect(selected);
          }} >
          Choose Media
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export const MediaUploadButton = ({ storyboard, target, mediaTypes, className, mediaTarget, name, handleActiveMedia }) => {

  const dispatch = useDispatch();
  const uploadError = useSelector(state => _.get(state, 'media.uploadError'));

  const handleUploadStart = (instances) => {
    instances.forEach(instance => {
      const upload = {
        id: instance.files[0].id,
        name: instance.files[0].name,
        type: instance.files[0].type,
        size: (instance.files[0].size / 1048576),
        progress: 0,
        instance: instance
      }

      dispatch(initiateMediaUpload(upload, mediaTarget));
    });
  }

  const handleUploadProgress = (file, progress) => {
    dispatch(updateMediaUploadProgress(file.id, progress));
  }

  const handleUploadComplete = (info, file) => {
    const formats = ['image/jpg', 'image/jpeg', 'image/png', 'image/svg', 'image/bmp', 'image/gif', 'image/svg+xml', 'video/mp4']
    if (info && formats.includes(file.type)) {
      var request = {
        fileName: file.name,
        contentType: file.type,
        signedUrl: info.signedUrl,
        width: _.get(file, 'dimension.width'),
        height: _.get(file, 'dimension.height'),
        storyboard: _.get(storyboard, 'id'),
        target: target,
      }

      if (file.type === 'video/mp4') {
        var video = document.createElement('video');

        video.onloadedmetadata = () => {
          request = {
            ...request,
            width: video.videoWidth,
            height: video.videoHeight
          }

          handleUploadMedia(request, file);
        };
        video.onerror = () => {

        };
        video.src = URL.createObjectURL(file);
      } else {
        handleUploadMedia(request, file);
      }
    }
  }

  const handleUploadMedia = (request, file) => {
    dispatch(uploadMedia(file.id, request, (response) => {
    }));
  }

  return (
    <FileUploadButton
      className={cx(styles.upload, className)}
      name="medaFile"
      multiple={true}
      accept={mediaTypes.map(type => {
        switch (type) {
          case 'image':
            return [
              "image/jpg",
              "image/jpeg",
              "image/png",
              "image/svg",
              "image/bmp",
              "image/gif",
              "image/svg+xml"]
          case 'video':
            return "video/mp4"
        }
      })}
      onStart={handleUploadStart}
      onProgress={handleUploadProgress}
      onComplete={handleUploadComplete}
      handleActiveMedia={handleActiveMedia}
    >
      <Button variant="primary"><i className="cleverstory-upload" />{name || "Upload Media"}</Button>
    </FileUploadButton>
  )
}

export const MediaGalleryView = ({ mediaTypes, storyboard, orientation, mediaTarget, target, onSelect, selected, editable, onInstanceChange }) => {
  const dispatch = useDispatch();

  const [searchQuery, setSearchQuery] = useState("");
  const [showError, setShowError] = useState(false);
  const uploads = useSelector(state => _.get(state, 'media.uploads')) || [];
  const mediaList = useSelector(state => _.get(state, 'media.items')) || [];
  const loading = useSelector(state => _.get(state, 'media.loading'));
  const pagination = useSelector(state => _.get(state, 'media.pagination'));
  const hasMoreItems = _.get(pagination, 'currentPage') < _.get(pagination, 'totalPages') - 1;
  const error = useSelector(state => _.get(state, 'media.error'));
  const uploadingFail = useSelector(state => _.get(state, 'media.uploadError', false));
  const [selectedFilters, setSelectedFilters] = useState({ sortType: "modifiedDate", sortOrder: "desc" });
  const errorType = useSelector(state => _.get(state,'media.errorType'));
  const filterTypeOption = [
    {
      label: "Name",
      value: "name",
    },
    {
      label: "Date",
      value: "modifiedDate",
    },
  ];

  useEffect(() => {
    return () => {
      dispatch(invalidateMediaLibrary());
    }
  }, []);

  const onFilterChange = (type, value) => {
    setSelectedFilters((state) => {
      return {
        ...state,
        [type]: value,
      };
    });
  };

  useEffect(() => {
    handleSearchMedia();
  }, [selectedFilters])

  useEffect(() => {
    if (error) {
      setShowError(true);
    }
  }, [error]);

  useEffect(() => {
    if(uploadingFail) {
      handleMediaUploadError(errorType ,handleUploadErrorClose);
    }
  }, [uploadingFail]);

  const handleSearchMedia = () => {
    const request = {
      storyboard: _.get(storyboard, 'id'),
      target: target,
      mediaTypes: mediaTypes,
      page: 0,
      limit: 20,
      sort: selectedFilters.sortType,
      order: selectedFilters.sortOrder,
      query: searchQuery,
    };

    dispatch(searchMedia(request));
  }

  const loadMoreMedia = () => {
    const nextPage = pagination.currentPage + 1;
    if (nextPage >= pagination.totalPages) {
      return;
    }

    const request = {
      storyboard: storyboard.id,
      target: target,
      page: pagination.currentPage + 1,
      mediaTypes: mediaTypes,
      limit: 20,
      sort: selectedFilters.sortType,
      order: selectedFilters.sortOrder,
      query: searchQuery,
    };

    dispatch(searchMedia(request));
  }

  const handleClick = (media) => {
    if (!editable) {
      return;
    }

    onSelect(media);
  }

  const handleErrorClose = () => {
    setShowError(false);
    dispatch(invalidateMediaDeleteError());
  }

  const MediaErrorMessage = ({ message, onClose }) => {
    return (
      <div className={styles.errorMessage}>
        {message}
        <Button variant="info" onClick={() => onClose()} className={styles.closeIcon}><i className="cleverstory-close" /></Button>
      </div>
    )
  }

  const handleMediaUploadError = (message, onClose) => {
    onClose();
    return toast(
      <FailureToast
        heading={`Failure`}
        description={message}
      />,
      {
        type: toast.TYPE.DEFAULT,
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: 3000,
      }
    );
  }

  const MediaEmptyMessage = () => {
    return (
      <div className={styles.emptyMediaContainer} >
        <div className={styles.emptyMessage}>
          <img className={cx(styles.emptyMediaImage)} alt="emptyMedia_image" />
          <div className={styles.errorMessageContent}>
            <div className={styles.errorMessageTitle}>
              <h2 className={styles.heading}>Weather forecast</h2>
            </div>
            <div className={styles.errorMessageSubText}>
              <span>Clear skies and <br /> empty screens</span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const handleUploadErrorClose = () => {
    dispatch(invalidateUploadError());
  }

  const handleUploadStart = (instances) => {
    instances?.forEach(instance => {
      const upload = {
        id: instance.files[0].id,
        name: instance.files[0].name,
        type: instance.files[0].type,
        size: (instance.files[0].size / 1048576),
        progress: 0,
        instance: instance
      }

      dispatch(initiateMediaUpload(upload, mediaTarget));
    });
  }

  const handleUploadProgress = (file, progress) => {
    dispatch(updateMediaUploadProgress(file.id, progress));
  }

  const handleUploadComplete = (info, file) => {
    const formats = ['image/jpg', 'image/jpeg', 'image/png', 'image/svg', 'image/bmp', 'image/gif', 'image/svg+xml', 'video/mp4']
    if (info && formats.includes(file.type)) {
      var request = {
        fileName: file.name,
        contentType: file.type,
        signedUrl: info.signedUrl,
        width: _.get(file, 'dimension.width'),
        height: _.get(file, 'dimension.height'),
        storyboard: _.get(storyboard, 'id'),
        target: target,
      }

      if (file.type === 'video/mp4') {
        var video = document.createElement('video');

        video.onloadedmetadata = () => {
          request = {
            ...request,
            width: video.videoWidth,
            height: video.videoHeight
          }

          handleUploadMedia(request, file);
        };
        video.onerror = () => {

        };
        video.src = URL.createObjectURL(file);
      } else {
        handleUploadMedia(request, file);
      }
    }
  }

  const handleFileCheck = (file,next) => {
    // limit is 15
    const fileSizeExceeded = (file.size / 1048576) > 15;

    if (file.type === 'video/mp4' && fileSizeExceeded) {
      console.log('video size is maximum')
    } else {
      next(file);
    }
  }

  const handleUploadMedia = (request, file) => {
    dispatch(uploadMedia(file.id, request, () => {
    }));
  }

  const removeSpecialCharactersFromName = (name)=>{
    return name.replace(/\u202F/g, '');
  }

  const handleFileDropRejected = (fileRejections) => {
    fileRejections?.forEach(() => {
        const upload = {
          type: "",
          size: 0,
        }
  
        dispatch(initiateMediaUpload(upload, mediaTarget));
    });
  }

  const handleFileDrop = (acceptedFiles) => {
    const uploaders = Array.from(acceptedFiles).map(file => {
      file.id = shortid();
      var updatedName = removeSpecialCharactersFromName(file.name)

      var s3Uploader = new S3Upload({
        signingUrl: "/file_upload/initiate",
        signingUrlMethod: "POST",
        signingUrlHeaders: {
          Authorization: `Bearer ${AuthService.getAccessToken()}`
        },
        server: API_URL,
        scrubFilename: (filename) => {
          var name = removeSpecialCharactersFromName(filename);
          name = name.replace(/[^\w\d_\-.\s]+/ig, '')
          return name;
        },
        signingUrlQueryParams: {'originalName': encodeURIComponent(updatedName)},
        contentDisposition: 'auto',
        preprocess:handleFileCheck,
        uploadRequestHeaders: {},
        onFinishS3Put: handleUploadComplete,
        onProgress: (progress, _textState, file) => handleUploadProgress(file, progress),
        onError: () => {}, 
        files: [file]
      });

      return s3Uploader;
    });

    handleUploadStart(uploaders);
  }

  const activeDropdownLabel = _.find(filterTypeOption, (data)=>{
    return data.value == selectedFilters.sortType;
  })

  return (
    <div className={cx(styles.right)}>
      <div className={cx(styles.searchMediaHeader)}>
        <div className={styles.search}>
          <InputGroup className={styles.searchBox}>
            <Form.Control
              className={styles.searchInput}
              placeholder="Search in media"
              aria-label="Search in media"
              aria-describedby="<Media> search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearchMedia();
                }
              }}
            />
            <InputGroup.Text
              className={styles.searchIcon}
              id="basic-addon1"
              onClick={handleSearchMedia}
            >
              <i className="cleverstory-search" />
            </InputGroup.Text>
          </InputGroup>
        </div>
        <div className={styles.sortMedia}>
          <label className={styles.filterLabel}>Sort by:</label>
          <label className={styles.filterLabel}>
            {selectedFilters.sortOrder && <img onClick={() => onFilterChange("sortOrder", selectedFilters.sortOrder === "ASC" ? "DESC" : "ASC")} className={cx(styles.sortIcon, selectedFilters.sortOrder === "ASC" ? styles.ascending : styles.descending)} alt="sortIcon" src='/images/sortIcon.svg' />}
          </label>
          <div className={styles.sortbyLabel}>
            <Dropdown className={styles.storboardTypeDropdown}>
              <Dropdown.Toggle id="dropdown-custom-components">
                {activeDropdownLabel.label}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {filterTypeOption.map((option, index) => {
                  return (
                    <Dropdown.Item
                      active={option.value == selectedFilters.sortType}
                      onClick={() => onFilterChange("sortType", option.value)}
                      key={index}
                      eventKey={index}
                    >
                      {option.label}
                    </Dropdown.Item>
                  );
                })}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </div>
      <InfiniteScroll
        className={cx(styles.scrollable, { [styles.editableSroll]: editable })}
        loading={loading}
        hasMore={hasMoreItems}
        loadMore={() => {
          loadMoreMedia();
        }}
      >
        <Dropzone noClick={true} onDrop={handleFileDrop} multiple={true} filesLimit={null} noDragEventsBubbling={true} onDropRejected={handleFileDropRejected} accept={mediaTypes.map(type => {
          switch (type) {
            case 'image':
              return [
                "image/jpg",
                "image/jpeg",
                "image/png",
                "image/svg",
                "image/bmp",
                "image/gif",
                "image/svg+xml"]
            case 'video':
              return ["video/mp4"]
            }
          })}>
          {({ getRootProps }) => (
            <div className={cx(styles.grid)} {...getRootProps({})}>
              {uploads.map((upload) => {
                return (
                  <MediaUploadIndicator
                    key={upload.id}
                    media={upload}
                    instance={upload.instance}
                    editable={editable}
                    onDimensionChange={(dimension) => {
                      upload.instance.files[0].dimension = dimension;
                      onInstanceChange(upload.instance);
                    }}
                  />
                );
              })}

              {mediaList.length > 0 &&
                mediaList.map((media) => {
                  let active = null;

                  if (editable) {
                    active = selected.find((m) => m.id === media.id) != null;
                  }

                  if (media.type === "image") {
                    return (
                      <Media
                        key={media.id}
                        media={media}
                        selected={active}
                        editable={editable}
                        onClick={() => {
                          handleClick(media);
                        }}
                      />
                    );
                  }

                  if (media.type === "video") {
                    return (
                      <Video
                        key={media.id}
                        muted
                        media={media}
                        selected={active}
                        editable={editable}
                        onClick={() => {
                          handleClick(media);
                        }}
                      />
                    );
                  }
                })}
              {mediaList.length == 0 && uploads.length == 0 && (
                <MediaEmptyMessage />
              )}
            </div>
          )}
        </Dropzone>
      </InfiniteScroll>
      {showError && _.size(error) > 0 &&
        <MediaErrorMessage message='The media cannot be deleted as it exists in one or more pages.' onClose={handleErrorClose} />
      }
    </div>
  )
}

const Media = ({ media, selected, onClick, onDelete, editable }) => {
  const dispatch = useDispatch();

  const deleting = useSelector(state => _.get(state, 'media.deleting'));

  const [confirm, showConfirm] = useState(false);

  return (
    <div className={cx(styles.media, { [styles.selected]: selected, [styles.deleting]: confirm && deleting })} onClick={onClick}>
      <div className={styles.icon}>
        <IKImage path={media.url}
          transformation={[{
            "width": "600"
          }]}
          loading="lazy" />
        <div className={styles.overlay}>
          <div className={styles.actions}>
            <button onClick={(e) => {
              if(!selected) e.stopPropagation();
              showConfirm(true);
            }} className={styles.action}>
              <i className={cx(styles.trashIcon, { [styles.disabled]: confirm }, 'cleverstory-trash')} />
            </button>
          </div>
          {confirm &&
            <div className={styles.deleteMediaConfimation}>
              <div className={styles.deleteMedia}>
                <Button
                  variant="secondary"
                  className={styles.cancelButton}
                  onClick={() => { showConfirm(false); }}>Cancel</Button>
                <Button variant="primary" className={styles.deleteButton} onClick={(e) => {
                  e.stopPropagation();
                  dispatch(deleteMedia(media.id));
                }}>Confirm</Button>
              </div>
            </div>
          }
        </div>
      </div>
      <div className={styles.name}>
        <i className={cx("cleverstory-background")} />
        <span className={styles.label}>
          <Truncate lines={1}>{media.name}</Truncate>
        </span>
      </div>
    </div>
  );
}

const Video = ({ media, selected, onClick, onDelete, editable }) => {
  const dispatch = useDispatch();

  const deleting = useSelector(state => _.get(state, 'media.deleting'));

  const videoRef = React.useRef(null);

  const [confirm, showConfirm] = useState(false);

  useEffect(() => {
    if (selected) {
      videoRef.current.play();
    } else {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [selected]);

  return (
    <div className={cx(styles.media, { [styles.selected]: selected, [styles.deleting]: confirm && deleting })} onClick={onClick}>
      <div className={styles.icon}>
        <video name="media" ref={videoRef} className={styles.videoPlayer} autoPlay loop muted>
          <source src={`${IMAGE_CDN_ENDPOINT}/${media.url}`} />
        </video>
        <div className={styles.overlay}>
          <div className={styles.actions}>
            <button onClick={() => { showConfirm(true); }} className={styles.action}>
              <i className={cx(styles.trashIcon, { [styles.disabled]: confirm }, 'cleverstory-trash')} />
            </button>
          </div>
          {confirm &&
            <div className={styles.deleteMediaConfimation}>
              <div className={styles.deleteMedia}>
                <Button
                  variant="secondary"
                  className={styles.cancelButton}
                  onClick={() => { showConfirm(false); }}>Cancel</Button>
                <Button variant="primary" className={styles.deleteButton} onClick={(e) => {
                  e.stopPropagation();
                  dispatch(deleteMedia(media.id));
                  showConfirm(false);
                }}>Confirm</Button>
              </div>
            </div>
          }
        </div>
      </div>
      <div className={styles.name}>
        <i className={cx("cleverstory-video")} />
        <span className={styles.label}>
          <Truncate lines={1}>{media.name}</Truncate>
        </span>
      </div>
    </div>
  )
}

export const MediaUploadIndicator = ({ media, instance, onDimensionChange, className, editable }) => {
  const imageRef = React.useRef(null);
  const videoRef = React.useRef(null);

  const fileType = _.get(instance, 'files[0].type', 'image');
  const type = fileType === 'video/mp4' ? 'video' : 'image';

  const previewFile = (file) => {
    if (!imageRef.current) {
      return;
    }

    var reader = new FileReader();

    reader.onloadend = function () {
      if (imageRef.current) {
        imageRef.current.src = reader.result;
      }
    }

    if (file) {
      reader.readAsDataURL(file);
    } else {
      imageRef.current.src = "";
    }
  }

  useEffect(() => {

    if (instance) {
      const file = instance.files[0];

      if (type === 'image' && imageRef) {
        imageRef.current.onload = onImageLoad;
        previewFile(file);
      }

      if (type === 'video' && videoRef) {
        var video = videoRef.current;
        var sources = video.getElementsByTagName('source');
        sources[0].src = URL.createObjectURL(file);
      }
    }
  }, [imageRef, videoRef]);

  const onImageLoad = () => {
    const dimension = {
      width: imageRef.current.naturalWidth,
      height: imageRef.current.naturalHeight
    }

    if (onDimensionChange) {
      onDimensionChange(dimension);
    }
  }

  return (
    <div className={cx(styles.media, styles.uploading, className)}>
      <div className={styles.icon}>
        {type === 'image' &&
          <img ref={imageRef} />
        }
        {type === 'video' &&
          <video ref={videoRef} name="media" className={styles.videoPlayer} autoPlay loop muted >
            <source />
          </video>
        }
        <div className={styles.overlay}>
          <div className={styles.actions}>
            {!media.processing &&
              <ProgressBar width={'100%'} variant="warning" now={media.progress} />
            }

            {media.processing &&
              <div className={styles.status}>Processing...</div>
            }
          </div>
        </div>
      </div>
      <div className={styles.name}>
      <i class={type === 'video' ? "cleverstory-video" : "cleverstory-background"}></i>
        <span className={styles.label}>
          <Truncate lines={1}>{media.name}</Truncate>
        </span>
      </div>
    </div>
  );
}